import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { settings } from "./data/settings";
import styled from "styled-components";
import { FaArrowUp } from "react-icons/fa";

import Navbar from "./components/Navbar";
import Footer from "./pages/sections/Footer";
import CookieBanner from "./components/CookieBanner";
import SEO from "./components/SEO";
import HeroSection from "./pages/sections/HeroSection";
import OffresSection from "./pages/sections/OffresSections";
import ContactSection from "./pages/sections/ContactSection";
import PresenceSection from "./pages/sections/PresenceSection";
import ProcessSection from "./pages/sections/ProcessSection";
import TestimonialsSection from "./pages/sections/TestimonialsSection";
import FAQSection from "./pages/sections/FAQSection";
import FinalCTASection from "./pages/sections/FinalCTASection";
import ProjectsSection from "./pages/sections/ProjectsSections";
import AboutSection from "./pages/sections/AboutSection";
import ScrollToHash from "./components/ScrollToHash";

import OffreMobilePage from "./pages/OffreMobilePage";
import OffreWebAppPage from "./pages/OffreWebAppPage";
import OffreBureauPage from "./pages/OffreBureauPage";
import OffreWebPage from "./pages/OffreWebPage";
import PageMentionsLegales from "./pages/PageMentionsLegales";
import PrixSiteWebPage from "./pages/PrixSiteWebPage";
import FreelanceOuAgencePage from "./pages/FreelanceOuAgencePage";
import DeveloppeurWebFreelanceParisPage from "./pages/DeveloppeurWebFreelanceParisPage";
import ProspectionTool from "./components/ProspectionTool";
import AdminRoute from "./admin/components/AdminRoute";
import AdminLayout from "./admin/components/AdminLayout";
import AdminLoginPage from "./admin/pages/AdminLoginPage";
import AdminDashboardPage from "./admin/pages/AdminDashboardPage";
import AdminLeadsPage from "./admin/pages/AdminLeadsPage";
import AdminLeadDetailPage from "./admin/pages/AdminLeadDetailPage";
import AdminEmailsPage from "./admin/pages/AdminEmailsPage";
import AdminSettingsPage from "./admin/pages/AdminSettingsPage";
import AdminSecurityPage from "./admin/pages/AdminSecurityPage";
import AdminAuditLogsPage from "./admin/pages/AdminAuditLogsPage";
import AdminProspectsPage from "./admin/pages/AdminProspectsPage";
import AdminProspectDetailPage from "./admin/pages/AdminProspectDetailPage";
import AdminProspectsWhyNotPage from "./admin/pages/AdminProspectsWhyNotPage";
import AdminProspectsPipelinePage from "./admin/pages/AdminProspectsPipelinePage";
import AdminProspectionSearchPage from "./admin/pages/AdminProspectionSearchPage";

// pour les conditions CSS
import { css } from "styled-components";

const SectionWrapper = styled.div`
  background-color: ${({ bg }) =>
    bg === "dark"
      ? "var(--my-color3)"
      : bg === "light"
      ? "var(--my-color1)"
      : "#ffffff"};
  width: 100% !important;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  overflow: hidden;
  padding: 3rem 0;
  ${({ full }) =>
    full &&
    css`
      padding: 0;
      margin: 0;
    `}
  margin-bottom: -1px;

  /* Slanted separators si prop slant fournie */
  ${({ slant }) => {
    if (!slant) return "";

    const map = {
      "top-right": "polygon(0 0, 100% 4vw, 100% 100%, 0 100%)",
      "top-left": "polygon(0 4vw, 100% 0, 100% 100%, 0 100%)",
      "bottom-right": "polygon(0 0, 100% 0, 100% 100%, 0 calc(100% - 4vw))",
      "bottom-left": "polygon(0 0, 100% 0, 100% calc(100% - 4vw), 0 100%)",
      "both-right": "polygon(0 0, 100% 4vw, 100% calc(100% - 4vw), 0 100%)",
      "both-left": "polygon(0 4vw, 100% 0, 100% 100%, 0 calc(100% - 4vw))",
    };

    let key = slant;
    if (slant === "top") key = "top-right";
    if (slant === "bottom") key = "bottom-right";
    if (slant === "both") key = "both-right";

    const path = map[key] || map["bottom-right"];

    return css`
      clip-path: ${path};
    `;
  }}

  /* Chevauchement pour combler le triangle retiré */
  ${({ slant }) =>
    slant &&
    slant.includes("top") &&
    css`
      margin-top: -4vw;
      padding-top: calc(3rem + 4vw);
    `}

  ${({ slant }) =>
    slant &&
    slant.includes("bottom") &&
    css`
      margin-bottom: -4vw;
      padding-bottom: calc(3rem + 4vw);
    `}
`;

const ScrollButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background-color: var(--my-color3);
  color: white;
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  transition: background-color 0.3s ease, transform 0.3s ease;

  &:hover {
    background-color: var(--my-color5);
    transform: scale(1.1);
  }
`;

const ScrollToTopButton = ({ show, onClick }) =>
  show ? (
    <ScrollButton onClick={onClick} aria-label="Scroll to top">
      <FaArrowUp />
    </ScrollButton>
  ) : null;

const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 200);
      }
    }
  }, [location]);

  return (
    <>
      <SEO
        title="Développeur freelance web, mobile et logiciel sur mesure | DigiBuddy"
        description="DigiBuddy accompagne indépendants, TPE et PME : site web sur mesure, application mobile et logiciel métier. Priorité Paris, intervention France et Europe."
        keywords="développeur freelance paris, développeur web freelance, création site internet sur mesure, développement application mobile, logiciel métier sur mesure, développeur freelance france, développeur web europe, refonte site web"
        url="https://www.digibuddy.fr"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://www.digibuddy.fr/#organization",
            name: "DigiBuddy",
            url: "https://www.digibuddy.fr",
            logo: "https://www.digibuddy.fr/favicon.png",
            description:
              "Développeur freelance web, mobile et logiciels sur mesure pour indépendants et PME.",
            founder: {
              "@type": "Person",
              name: "Vincent Deliencourt",
            },
            address: {
              "@type": "PostalAddress",
              addressLocality: "Paris",
              addressCountry: "FR",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+33 6 40 53 81 76",
              contactType: "Service client",
              availableLanguage: ["fr"],
            },
            areaServed: ["Paris", "France", "Europe"],
            serviceType: [
              "Création de site web sur mesure",
              "Développement d'applications mobiles",
              "Logiciels métier et applications web",
              "Refonte et maintenance web",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://www.digibuddy.fr/#website",
            url: "https://www.digibuddy.fr",
            name: "DigiBuddy",
            inLanguage: "fr-FR",
            publisher: { "@id": "https://www.digibuddy.fr/#organization" },
          },
        ]}
      />
      <SectionWrapper id="accueil" bg="white" full>
        <HeroSection />
      </SectionWrapper>
      <SectionWrapper id="presence" bg="light" slant="both-right">
        <PresenceSection />
      </SectionWrapper>
      <SectionWrapper id="about" bg="dark" slant="top-left">
        <AboutSection />
      </SectionWrapper>
      <SectionWrapper id="process" bg="dark" slant="bottom-right">
        <ProcessSection />
      </SectionWrapper>
      <SectionWrapper id="offres" bg="light" slant="top-right">
        <OffresSection />
      </SectionWrapper>
      <SectionWrapper id="projets" bg="light">
        <ProjectsSection />
      </SectionWrapper>
      <SectionWrapper id="testimonials" bg="white" slant="top-left">
        <TestimonialsSection />
      </SectionWrapper>
      <SectionWrapper id="faq" bg="white">
        <FAQSection />
      </SectionWrapper>
      <SectionWrapper id="final-cta" bg="dark" slant="both">
        <FinalCTASection />
      </SectionWrapper>
      <SectionWrapper id="contact" bg="white">
        <ContactSection />
      </SectionWrapper>
    </>
  );
};

const ProspectionLegacyPage = () => (
  <>
    <SEO
      title="Outil de prospection legacy (admin) | DigiBuddy"
      description="Version historique de l'outil de prospection."
      url="https://www.digibuddy.fr/admin/prospection/legacy"
      noIndex
      noFollow
    />
    <ProspectionTool />
  </>
);

const RouterContent = ({ showScroll, scrollToTop }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      {!isAdminRoute && <Navbar settings={settings} pages={settings?.pages} />}
      {!isAdminRoute && <CookieBanner />}

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/mentions-legales" element={<PageMentionsLegales />} />
        <Route
          path="/combien-coute-site-web-sur-mesure"
          element={<PrixSiteWebPage />}
        />
        <Route
          path="/freelance-ou-agence-pour-creer-son-site"
          element={<FreelanceOuAgencePage />}
        />
        <Route
          path="/developpeur-web-freelance-paris"
          element={<DeveloppeurWebFreelanceParisPage />}
        />
        <Route path="/creation-site-web-sur-mesure" element={<OffreWebPage />} />
        <Route
          path="/developpement-application-mobile"
          element={<OffreMobilePage />}
        />
        <Route
          path="/logiciel-de-bureau-sur-mesure"
          element={<OffreBureauPage />}
        />
        <Route path="/logiciel-metier-sur-mesure" element={<OffreWebAppPage />} />
        <Route
          path="/offres/web"
          element={<Navigate to="/creation-site-web-sur-mesure" replace />}
        />
        <Route
          path="/offres/mobile"
          element={<Navigate to="/developpement-application-mobile" replace />}
        />
        <Route
          path="/offres/logicielBureau"
          element={<Navigate to="/logiciel-de-bureau-sur-mesure" replace />}
        />
        <Route
          path="/offres/logicielWeb"
          element={<Navigate to="/logiciel-metier-sur-mesure" replace />}
        />
        <Route
          path="/prospection"
          element={<Navigate to="/admin/prospection" replace />}
        />

        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="leads" element={<AdminLeadsPage />} />
          <Route path="leads/:id" element={<AdminLeadDetailPage />} />
          <Route path="emails" element={<AdminEmailsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="audit-logs" element={<AdminAuditLogsPage />} />
          <Route path="security" element={<AdminSecurityPage />} />
          <Route path="prospection" element={<AdminProspectionSearchPage />} />
          <Route path="prospection/legacy" element={<ProspectionLegacyPage />} />
          <Route path="prospects" element={<AdminProspectsPipelinePage />} />
          <Route path="prospects/shortlist" element={<AdminProspectsPage />} />
          <Route path="prospects/why-not" element={<AdminProspectsWhyNotPage />} />
          <Route path="prospects/:id" element={<AdminProspectDetailPage />} />
        </Route>

        <Route path="/*" element={<Navigate to="/" replace />} />
      </Routes>

      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ScrollToTopButton show={showScroll} onClick={scrollToTop} />}
    </>
  );
};

const App = () => {
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <ScrollToHash />
        <RouterContent showScroll={showScroll} scrollToTop={scrollToTop} />
      </Router>
    </ThemeProvider>
  );
};

export default App;
